import React from 'react';
import { answerLabel, confirmedSummary, lastFour, type EventAnswers, type RsvpAnswer } from '../../utils/bulkRsvp';
import { sideLabel, normalizeSide } from '../../utils/guestSide';

export interface CardEvent { id: string; title: string }
export interface CardGuest { id: string; name: string; phone?: string | null; side?: string | null }

const STATUS_LABEL: Record<string, string> = { confirmed: 'Confirmed', declined: 'Declined', pending: 'Pending', awaiting_plus_one: 'Awaiting plus-one' };

interface Props {
  guest: CardGuest;
  events: CardEvent[];
  answers: EventAnswers;
  /** Events whose answer differs from what is saved. */
  changedEventIds: ReadonlySet<string>;
  /** The guest-level status as the server derived it. Shown as is; never worked out here. */
  status: string;
  staged: boolean;
  onCycle: (eventId: string) => void;
  onAll: () => void;
  onAllBut: (eventId: string) => void;
  onConfirmInvited: () => void;
  onToggleRemove: (staged: boolean) => void;
}

/** One guest: who they are, one button per project event, quick actions, and the Remove completely tick. */
const BulkRsvpCard: React.FC<Props> = ({ guest, events, answers, changedEventIds, status, staged, onCycle, onAll, onAllBut, onConfirmInvited, onToggleRemove }) => {
  const side = normalizeSide(guest.side);
  const digits = lastFour(guest.phone);
  const { confirmed, invited } = confirmedSummary(answers);
  const changed = changedEventIds.size > 0;
  return (
    <li className={`rsvp-card${changed ? ' rsvp-card--changed' : ''}${staged ? ' rsvp-card--staged' : ''}`}>
      <div className="rsvp-card__head">
        <div className="rsvp-card__who">
          <span className="rsvp-card__name">{guest.name}</span>
          <span className="rsvp-card__meta">
            {side ? sideLabel(side) : 'No side'} · {digits ? `Phone ends ${digits}` : 'No phone'}
          </span>
        </div>
        <span className={`rsvp-status rsvp-status--${status}`} title="Overall status, worked out by the server">{STATUS_LABEL[status] ?? status}</span>
      </div>
      <p className="rsvp-card__summary">{confirmed} of {invited} events confirmed</p>
      <div className="rsvp-card__events" role="group" aria-label={`Events for ${guest.name}`}>
        {events.map(ev => {
          const answer: RsvpAnswer = answers[ev.id] ?? null;
          const state = answerLabel(answer);
          return (
            <button
              key={ev.id} type="button" disabled={staged}
              className={`rsvp-event rsvp-event--${answer ?? 'none'}${changedEventIds.has(ev.id) ? ' rsvp-event--changed' : ''}`}
              aria-label={`${guest.name}, ${ev.title}: ${state}. Press to change.`}
              onClick={() => onCycle(ev.id)}
            >
              <span className="rsvp-event__title">{ev.title}</span>
              <span className="rsvp-event__state">{state}</span>
            </button>
          );
        })}
      </div>
      <div className="rsvp-card__foot">
        <div className="rsvp-quick">
          <button type="button" className="rsvp-quick__btn" data-quick="all" disabled={staged} onClick={onAll}>All events</button>
          <select
            className="rsvp-quick__select" data-quick="except" disabled={staged} value=""
            aria-label={`All events except one, for ${guest.name}`}
            onChange={e => { if (e.target.value) onAllBut(e.target.value); }}
          >
            <option value="">All but one event</option>
            {events.map(ev => <option key={ev.id} value={ev.id}>All but {ev.title}</option>)}
          </select>
          <button type="button" className="rsvp-quick__btn" data-quick="confirm" disabled={staged} onClick={onConfirmInvited}>Confirm invited</button>
        </div>
        <label className="rsvp-card__remove">
          <input type="checkbox" checked={staged} aria-label={`Remove ${guest.name} completely`} onChange={e => onToggleRemove(e.target.checked)} />
          Remove completely
        </label>
      </div>
    </li>
  );
};

export default React.memo(BulkRsvpCard);
